/**
 * Der Ausschnitt eines Projekt-Canvas — Position und Zoomstufe zusammen
 * (siehe „Ausschnitt" in CONTEXT.md). Er wird je Projekt gemerkt, damit ein
 * wieder geöffneter Canvas dort steht, wo man ihn verlassen hat.
 *
 * **Nur auf diesem Gerät.** Der Ausschnitt hängt an der Fenstergröße: dieselben
 * Zahlen zeigen auf einem 13-Zoll-Laptop etwas anderes als auf einem
 * 27-Zoll-Monitor. Ihn über die Geräte hinweg zu teilen brächte also nicht
 * dieselbe Ansicht, sondern nur dieselben Zahlen — deshalb `localStorage` und
 * nicht ein Feld am Projekt, wie es `canvasPanelState` nutzt.
 *
 * Bewusst ohne React-Bezug: Lesen, Schreiben und Prüfen sind reine Rechnung
 * und sollen ohne gerenderten Canvas prüfbar sein. Die React-Hälfte steht in
 * `hooks/useCanvasViewport.ts`.
 */

import type { Viewport } from '@xyflow/react'
import { buildTabOrder, type TabOrderRect } from './canvasTabOrder'

/**
 * Die Zoomgrenzen des Canvas. Sie stehen HIER und nicht in `CanvasFlow`, weil
 * beide sie brauchen und `CanvasFlow` seinerseits den Hook braucht — dort
 * exportiert gäbe es einen Zirkelbezug.
 *
 * Rauszoomen bis 0,3, um den Überblick zu behalten. Näher als die
 * 1:1-Ansicht kommt man nicht heran: darüber hinaus gäbe es nichts zu sehen,
 * der Canvas hat keine Inhalte mit feineren Details.
 */
const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

export const CANVAS_MIN_ZOOM = 0.3
export const CANVAS_MAX_ZOOM = 1

/** Wo ein Canvas ohne gemerkten Ausschnitt beginnt. */
export const DEFAULT_CANVAS_VIEWPORT: Viewport = { x: 0, y: 0, zoom: 1 }

/**
 * Der Faktor, mit dem alles gegengerechnet wird, was bei jeder Zoomstufe
 * gleich AUSSEHEN soll — Auswahlrahmen, Eckpunkte, Hilfslinien.
 *
 * Der Canvas skaliert seinen Inhalt; ein 2px-Strich wäre bei 30 % nur noch
 * 0,6 Bildschirmpixel und verschwände. Mal diesen Faktor bleibt er 2.
 *
 * Dieselbe Formel wie React Flow für seine eigenen Griffe
 * (`Math.max(1 / zoom, 1)`, @xyflow/react index.js:4807) — weicht sie ab,
 * sehen unsere Elemente anders aus als seine. Nach oben gedeckelt bei 1:
 * Hereingezoomt sollen die Striche nicht DÜNNER werden als gezeichnet.
 *
 * An EINER Stelle, weil beide Auswahlarten sie brauchen: die einzelne Notiz
 * (über die CSS-Variable `--canvas-scale`) und der gemeinsame Rahmen (über
 * `--selection-scale`). Liefen sie auseinander, sähe eine ausgewählte Notiz
 * anders aus als dieselbe Notiz in einer Mehrfachauswahl.
 */
export function canvasCounterScale(zoom: number): number {
  return Math.max(1 / zoom, 1)
}

/**
 * Die Strichstärke eines **Auswahlrahmens**, in Bildschirmpixeln — der einzelnen
 * Notiz wie der Mehrfachauswahl.
 *
 * **Bei 1:1 volle 2px, ganz herausgezoomt 1,5px**, dazwischen linear.
 *
 * Herausgezoomt schrumpfen die Zettel, die Striche aber nicht: Bei 30 % ist
 * eine Standardnotiz 50 Bildschirmpixel breit, und zwei ineinanderstehende
 * 2px-Rahmen machen daraus ein Gitter aus Linien, in dem die Notiz untergeht.
 * Ein halber Pixel weniger genügt, damit die Auswahl dort zurücktritt; bei 1:1
 * gibt es nichts zurückzutreten, und sie ist so kräftig wie zuvor.
 *
 * **Eine Zahl für BEIDE Rahmen**, aus demselben Grund wie
 * {@link canvasCounterScale} eine ist (CONTEXT.md, „Auswahlrahmen"): Liefen sie
 * auseinander, sähe eine ausgewählte Notiz anders aus als dieselbe Notiz in
 * einer Mehrfachauswahl.
 *
 * Die **Ziehpunkte** gibt es nicht mit: Sie sind Bedienung und müssen greifbar
 * bleiben, während der Rahmen nur Rückmeldung ist.
 *
 * Der Verlauf hängt an {@link CANVAS_MIN_ZOOM}: „ganz herausgezoomt" ist, was
 * der Canvas eben zulässt. Wer die Grenze verschiebt, verschiebt den Verlauf
 * mit, und das ist richtig so.
 */
/**
 * Die Deckkraft eines **Randpfeils**, als Anteil.
 *
 * **Bei 1:1 60 %, ganz herausgezoomt voll**, dazwischen linear.
 *
 * Die 60 % sind der Ton, in dem der Pfeil zurücktritt: Er ist eine
 * Nebenbemerkung, kein Bedienelement, und soll die Notiz nicht die Schau
 * stehlen. Herausgezoomt kehrt sich das um — dort schrumpfen die Notizen auf
 * ein Drittel, es liegen viel mehr im Bild, und ein blasser Pfeil am Rand geht
 * darin unter. Genau dann wird er aber am ehesten gebraucht: Je weiter man
 * draußen ist, desto eher liegt etwas jenseits des **Ausschnitts**.
 *
 * Gegenläufig zu {@link selectionStrokeWidth}, die beim Herauszoomen NACHGIBT
 * — und das ist kein Widerspruch: Der Auswahlrahmen steht mitten im Inhalt und
 * konkurriert mit ihm, der Randpfeil steht am Rand, wo sonst nichts ist.
 *
 * Der Verlauf hängt an {@link CANVAS_MIN_ZOOM}, wie dort auch.
 */
export function hintOpacity(zoom: number): number {
  const span = 1 - CANVAS_MIN_ZOOM
  const t = span > 0 ? clamp((1 - zoom) / span, 0, 1) : 0
  return 0.6 + 0.4 * t
}

/**
 * Ob dieser Zoom als **ganz nah** gilt — die Frage, an der der Doppelklick auf
 * die Fläche seine Richtung entscheidet.
 *
 * Mit Toleranz und nicht auf Gleichheit: `zoomTo` und `setCenter` landen nach
 * ihrer Animation auf Gleitkommazahlen, die knapp danebenliegen können. Ein
 * Doppelklick, der dann noch einmal hineinzoomt, sähe aus wie ein
 * verschluckter Anschlag.
 */
export function isZoomedIn(zoom: number): boolean {
  return zoom >= CANVAS_MAX_ZOOM - 0.01
}

/**
 * Ob dieser Zoom als **ganz weit weg** gilt — die andere Endlage desselben
 * Doppelklicks.
 *
 * Daran hängt die **Karte**: Ganz herausgezoomt ist sie IMMER da, gleich wo
 * man steht. Wer so weit hinausgeht, sucht Übersicht — und genau die gibt sie.
 * In dieser Lage ist außerdem oft alles im Bild und nach der sonstigen Regel
 * („kein Element angekommen") käme sie nicht.
 *
 * Dieselbe Toleranz wie bei `isZoomedIn` und aus demselben Grund: `zoomTo`
 * landet nach seiner Animation auf Gleitkommazahlen, die knapp danebenliegen.
 */
export function isZoomedOut(zoom: number): boolean {
  return zoom <= CANVAS_MIN_ZOOM + 0.01
}

export function selectionStrokeWidth(zoom: number): number {
  const span = 1 - CANVAS_MIN_ZOOM
  const t = span > 0 ? clamp((1 - zoom) / span, 0, 1) : 0
  return 2 - 0.5 * t
}

const STORAGE_KEY_PREFIX = 'happytask:canvas:viewport:'

const storageKeyFor = (projectId: string): string => `${STORAGE_KEY_PREFIX}${projectId}`


/**
 * Der gemerkte Ausschnitt eines Projekts, oder die Vorgabe.
 *
 * **Geprüft wird beim Lesen, nicht beim Schreiben.** Ein Eintrag kann aus einer
 * älteren Fassung stammen, von Hand verändert oder halb geschrieben sein — und
 * er landet ungeprüft in React Flows d3-Zoom-Instanz (`defaultViewport` wird
 * beim Aufbau gesetzt, ohne durch die Grenzen zu laufen). Ein Zoom jenseits der
 * Obergrenze öffnete den Canvas also zu nah und schnappte erst bei der ersten
 * Geste zurück.
 */
export function readCanvasViewport(projectId: string): Viewport {
  let raw: string | null
  try {
    raw = localStorage.getItem(storageKeyFor(projectId))
  } catch {
    // Im privaten Fenster wirft schon das Lesen.
    return DEFAULT_CANVAS_VIEWPORT
  }
  if (!raw) return DEFAULT_CANVAS_VIEWPORT

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_CANVAS_VIEWPORT

    const { x, y, zoom } = parsed as { x?: unknown; y?: unknown; zoom?: unknown }
    if (
      typeof x !== 'number' || !Number.isFinite(x) ||
      typeof y !== 'number' || !Number.isFinite(y) ||
      typeof zoom !== 'number' || !Number.isFinite(zoom)
    ) {
      return DEFAULT_CANVAS_VIEWPORT
    }

    // Der Zoom wird GEKLEMMT, nicht verworfen: ein Eintrag aus einer Fassung
    // mit anderen Grenzen soll seine Position behalten, statt den ganzen
    // Ausschnitt zu kosten.
    return { x, y, zoom: clamp(zoom, CANVAS_MIN_ZOOM, CANVAS_MAX_ZOOM) }
  } catch {
    return DEFAULT_CANVAS_VIEWPORT
  }
}

export function writeCanvasViewport(projectId: string, viewport: Viewport): void {
  try {
    localStorage.setItem(
      storageKeyFor(projectId),
      JSON.stringify({
        x: Math.round(viewport.x),
        y: Math.round(viewport.y),
        zoom: Math.round(viewport.zoom * 1000) / 1000,
      }),
    )
  } catch {
    // Kein Speicher (privates Fenster, volles Kontingent). Der Ausschnitt ist
    // Bequemlichkeit, kein Inhalt — das Scheitern darf niemanden stören.
  }
}

/**
 * Der Einzug des ersten Zettels von der linken Kante der Canvas-Fläche.
 *
 * Er hält die Platte der **Werkzeug-Pille** frei: Sie misst 80px
 * (`$toolbar-width + 2 * $toolbar-plate-padding` in `canvasLayers.scss`) und
 * sitzt 8px von der Kante (`$toolbar-plate-edge`) — zusammen 88. Die acht
 * Pixel darüber sind Luft, damit der Zettel nicht an ihr klebt. Ein
 * Invarianten-Test hält die Zahlen zusammen.
 *
 * Die Pille kann auch rechts sitzen (`canvasToolbarSide`); der Einzug bleibt
 * trotzdem konstant. 96px von links sind ohnehin die angenehmere Lesestelle
 * als die nackte Kante, und eine Zahl, die von der Seite der Pille abhinge,
 * ließe den ersten Blick je nach Einstellung woanders landen.
 */
export const FIRST_GLANCE_INSET_X = 96

/**
 * Derselbe Einzug von oben. Kleiner als links: Oben liegt keine Platte, nur
 * die Kopfzeile mit Projekttitel und Aktionsgruppe.
 */
export const FIRST_GLANCE_INSET_Y = 64

/**
 * Über diesen Anteil der Fläche hinaus wird der Einzug nicht genommen.
 *
 * Auf einem schmalen Canvas (geteilt, kleines Fenster) läge der Zettel sonst
 * schon halb wieder draußen — und die Zahl, die ihn freistellen soll, schöbe
 * ihn hinaus.
 */
const FIRST_GLANCE_MAX_INSET_RATIO = 0.25

/**
 * **Der erste Blick** — der Ausschnitt, mit dem ein Projekt aufgeht, für das
 * noch nichts gemerkt ist (`.claude/tasks/erster-blick-auf-die-zettel.md`).
 *
 * Er landet auf dem **ersten Zettel der Tab-Reihenfolge**, also oben links wie
 * beim Lesen, und lässt den Zoom auf 1. Von dort führt Tab durch alle weiteren.
 *
 * **Warum nicht einpassen.** Bei 50–100 Zetteln — Svens übliche Projektgröße —
 * liefe der Zoom bis an `CANVAS_MIN_ZOOM`, und die Zettel wären Briefmarken.
 * Lieber ein echter Zettel in voller Größe als die ganze Landkarte in
 * Miniatur (Entscheidung mit Sven, 2026-09-07).
 *
 * **Warum nicht mittig.** Der erste Zettel IST der oberste linke; zentrierte
 * man ihn, wäre die linke Bildhälfte leeres Papier. Er sitzt deshalb auf einem
 * Einzug, der die Werkzeug-Pille freihält.
 *
 * `null` heißt „hier ist nichts zu setzen": kein Zettel, auf den man zielen
 * könnte, oder eine Fläche, die noch nicht gemessen ist (TabStack-Falle,
 * CLAUDE.md — hinter `display: none` misst alles 0, und ein Ausschnitt, gegen
 * 0 gerechnet, stünde danach dauerhaft falsch).
 */
export function firstGlanceViewport(
  rects: TabOrderRect[],
  size: { width: number; height: number },
): Viewport | null {
  if (rects.length === 0) return null
  if (size.width <= 0 || size.height <= 0) return null

  const first = rects.find(r => r.id === buildTabOrder(rects)[0])
  if (!first) return null

  const insetX = Math.min(FIRST_GLANCE_INSET_X, size.width * FIRST_GLANCE_MAX_INSET_RATIO)
  const insetY = Math.min(FIRST_GLANCE_INSET_Y, size.height * FIRST_GLANCE_MAX_INSET_RATIO)

  // Bei Zoom 1 ist die Bildschirmlage schlicht `Flusslage + Verschiebung`.
  return { x: insetX - first.x, y: insetY - first.y, zoom: 1 }
}
